import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FormButtons from "components/common/FormButtons";
import SingleSelect from "components/common/SingleSelect";
import { addBranchValues } from "components/constants/InitialFormValues";
import { ErrorMessage, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  GoogleMap,
  Autocomplete,
  useJsApiLoader,
  MarkerF,
} from "@react-google-maps/api";
import FileUpload from "components/common/FileUpload";
import { addBranchSchema } from "components/common/Validation";
import { useDispatch, useSelector } from "react-redux";
import { getCityList } from "store/actions/citySlice";
import {
  addBranch,
  getBranchDetails,
  updateBranch,
} from "store/actions/branchSlice";
import { showAlert } from "store/reducers/alertReducer";
import { useNavigate, useSearchParams } from "react-router-dom";
import Spinners from "components/common/Spinners";

// Map container styles
const containerStyle = {
  width: "100%",
  height: "540px",
  borderRadius: 20,
  outline: "none",
};

// Default center (Riyadh)
const defaultCenter = {
  lat: 24.7136,
  lng: 46.6753,
};

const AddBranch = ({ mode }) => {
  const { t } = useTranslation();
  document.title = t("Add Branch | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { citiesList } = useSelector((state) => state.Cities);
  const { isLoading, loading, branchDetails } = useSelector(
    (state) => state.Branches
  );
  const [formValues, setFormValues] = useState(addBranchValues);

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [autocomplete, setAutocomplete] = useState(null);
  // Load Google Maps script using useJsApiLoader
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyADIaQz6BiMxdC5nHOuGixS_S-3Z5PELis",
    libraries: ["places"],
  });

  const mapOptions = {
    componentRestrictions: { country: "SA" }, // Restrict to Saudi Arabia
  };

  useEffect(() => {
    // GET CITY LIST
    const fetchCityList = () => {
      dispatch(getCityList());
    };
    fetchCityList();

    // GET DETAILS IF EDIT MODE
    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getBranchDetails(payload));
    };
    mode === "edit" && getDetails();
  }, []);

  // UPDATE FORM FIELD IN EDIT MODE
  useEffect(() => {
    if (branchDetails && !loading && mode === "edit") {
      const {
        name,
        nameAr,
        address,
        addressAr,
        cityId,
        imageUrls,
        status,
        latitude,
        longitude,
        mapLocation,
        wifiNetworkName,
        wifiNetworkPassword,
        lockMac,
      } = branchDetails || {};
      setFormValues({
        ...formValues,
        name,
        nameAr,
        address,
        addressAr,
        cityId: cityId?._id,
        latitude,
        longitude,
        imageUrls,
        status,
        mapLocation,
        wifiNetworkName,
        wifiNetworkPassword,
        lockMac,
      });

      const updatedLocation = {
        lat: latitude,
        lng: longitude,
      };
      setMapCenter(updatedLocation);
    }
  }, [branchDetails]);

  const handleOnSubmit = (values, { resetForm }) => {
    const {
      name,
      nameAr,
      address,
      addressAr,
      cityId,
      latitude,
      longitude,
      imageUrls,
      status,
      mapLocation,
      wifiNetworkName,
      wifiNetworkPassword,
      lockMac,
    } = values;
    const buttonId = document.activeElement.id;
    // UPDATE STATUS ACCORDING TO ACTION TYPE
    const updatedStatus =
      buttonId === "draftBtn" ? 3 : buttonId === "submitBtn" ? 1 : 1;

    let data = new FormData();
    data.append("name", name);
    data.append("nameAr", nameAr);
    // data.append("address", address);
    // data.append("addressAr", addressAr);
    data.append("cityId", cityId);
    data.append("latitude", latitude);
    data.append("longitude", longitude);
    data.append("wifiNetworkName", wifiNetworkName);
    data.append("wifiNetworkPassword", wifiNetworkPassword);
    data.append("lockMac", lockMac);
    const filesToUpload = imageUrls.map((fileObj) => fileObj);
    filesToUpload.forEach((file) => {
      data.append("imageUrls", file);
    });
    data.append("status", updatedStatus);
    data.append("mapLocation", mapLocation || "");

    if (mode === "add") {
      // data.append("branchId", randomBranchId.toString());
      dispatch(addBranch(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      data.append("id", id);
      dispatch(updateBranch(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  // Autocomplete instance load handler
  const onAutocompleteLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  // Handle place selection from the autocomplete dropdown
  const onPlaceChanged = (setFieldValue) => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (!place || !place.geometry) {
        console.error("Place data is not available or does not have geometry.");
        return;
      }

      const newCenter = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      const formattedAddress = place.formatted_address || place.name;

      setFieldValue("mapLocation", formattedAddress);
      setFieldValue("latitude", place?.geometry?.location?.lat());
      setFieldValue("longitude", place?.geometry?.location?.lng());

      setMapCenter(newCenter);
    } else {
      console.error("Autocomplete is not initialized.");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_branch">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={mode === "add" ? t("Add Branch") : t("Edit Branch")}
              />
            </Row>
            <Formik
              initialValues={formValues}
              validationSchema={addBranchSchema}
              enableReinitialize={true}
              onSubmit={handleOnSubmit}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  <Row className="common_wrapper">
                    <Col lg={6}>
                      <Row className="inner_section">
                        <Col lg={12}>
                          <h2 className="fs_20 mb-3">
                            {t("Branch Information")}
                          </h2>
                          <div className="form_field mb-0"></div>
                          <FileUpload
                            uploadedFiles={values.imageUrls || []}
                            setUploadedFiles={(files) =>
                              setFieldValue("imageUrls", files)
                            }
                            maxFiles={5}
                            maxFileSize={0.3 * 1024 * 1024}
                            acceptedFileTypes={[
                              "image/png",
                              "image/jpeg",
                              "image/svg+xml",
                            ]}
                            requireSquare={true}
                            requireRectangle={false}
                            // onChange={handleFilesChange}
                            className={
                              touched?.imageUrls && errors.imageUrls
                                ? "invalid"
                                : ""
                            }
                          />
                        </Col>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="name"
                              label={t("Branch English Name")}
                            />
                            <CustomField
                              name="name"
                              type="text"
                              placeholder={t("Enter Branch English Name")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="nameAr"
                              label={t("Branch Arabic Name")}
                            />
                            <CustomField
                              name="nameAr"
                              type="text"
                              placeholder={t("Enter Branch Arabic Name")}
                              touched={touched}
                              errors={errors}
                              isArabic={true}
                            />
                          </div>
                        </Col>
                        {/* <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="address"
                              label={t("English Address")}
                            />
                            <CustomField
                              name="address"
                              placeholder="Enter English Address"
                              touched={touched}
                              errors={errors}
                              as="textarea"
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="addressAr"
                              label={t("Arabic Address")}
                            />
                            <CustomField
                              name="addressAr"
                              placeholder="Enter Arabic Address"
                              touched={touched}
                              errors={errors}
                              as="textarea"
                              isArabic={true}
                            />
                          </div>
                        </Col> */}
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel htmlFor="cityId" label={t("City")} />
                            <SingleSelect
                              options={citiesList?.map((item) => ({
                                name: item?.name,
                                value: item?._id,
                              }))}
                              value={values.cityId}
                              placeholder={t("City")}
                              handleSingleSelectChange={(e) => {
                                setFieldValue("cityId", e.value);
                              }}
                              className={`${
                                errors.cityId && touched.cityId ? "invalid" : ""
                              }`}
                              error={errors?.cityId}
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="Location"
                              label={t("Location")}
                            />
                            {!isLoaded ? (
                              <div>Loading Google Maps...</div>
                            ) : loadError ? (
                              <div>Error loading Google Maps</div>
                            ) : (
                              <Autocomplete
                                onLoad={onAutocompleteLoad}
                                onPlaceChanged={() =>
                                  onPlaceChanged(setFieldValue)
                                }
                                options={mapOptions}
                              >
                                <input
                                  type="text"
                                  placeholder={t("Search for locations...")}
                                  value={values.mapLocation}
                                  className={`form-control ${
                                    touched.latitude && errors.latitude
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "mapLocation",
                                      e.target.value || ""
                                    );
                                    if (!e.target.value) {
                                      setFieldValue("latitude", "");
                                      setFieldValue("longitude", "");
                                    }
                                  }}
                                />
                              </Autocomplete>
                            )}
                            <ErrorMessage
                              name={"latitude"}
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="lockMac"
                              label={t("Lock Mac")}
                            />
                            <CustomField
                              name="lockMac"
                              type="text"
                              placeholder={"0C:00:F5:94:33:3D"}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row className="inner_section mt-3">
                        <h2 className="fs_20 mb-3">{t("WiFi Information")}</h2>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="wifiNetworkName"
                              label={t("WiFi Network Name")}
                            />
                            <CustomField
                              name="wifiNetworkName"
                              type="text"
                              placeholder={t("Enter WiFi Network Name")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={12}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="wifiNetworkPassword"
                              label={t("WiFi Password")}
                            />
                            <CustomField
                              name="wifiNetworkPassword"
                              type="text"
                              placeholder={t("WiFi Password")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col lg={6}>
                      {!isLoaded ? (
                        <div>Loading Google Maps...</div>
                      ) : loadError ? (
                        <div>Error loading Google Maps</div>
                      ) : (
                        <GoogleMap
                          mapContainerStyle={containerStyle}
                          center={mapCenter}
                          zoom={12}
                        >
                          {/* Marker at the selected location */}
                          <MarkerF position={mapCenter} />
                        </GoogleMap>
                      )}
                    </Col>
                  </Row>
                  <Row className="px-0 my-4">
                    <FormButtons
                      isDraftBtn={true}
                      isLoading={isLoading}
                      addBtnText={mode === "add" ? t("Add New") : t("Update")}
                    />
                  </Row>
                </Form>
              )}
            </Formik>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddBranch;
